import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TiktokOpsPager, { TIKTOK_OPS_DEFAULT_PAGE_SIZE } from '@/tiktok-platform/components/TiktokOpsPager';
import { togglePageSelection } from '@/tiktok-platform/utils/tiktokPageSelection';
import TiktokSyncRangeModal from '@/tiktok-platform/components/TiktokSyncRangeModal';
import TiktokDateRangeFilter, {
  TIKTOK_RETURN_TIME_TYPE_OPTIONS,
  pickDatePart,
  toDayEndDateTime,
  toDayStartDateTime,
  validateDateRange,
} from '@/tiktok-platform/components/TiktokDateRangeFilter';
import BatchQueryModal from '@/tiktok-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/tiktok-platform/modules/products/components/batchQueryUtils';
import { ProductFilterCard, ProductListCard } from '@/tiktok-platform/modules/products/components/ProductPageCards';
import TiktokOpsListToolbar, { downloadCsv } from '@/tiktok-platform/components/TiktokOpsListToolbar';
import TiktokSyncProgressSlot from '@/tiktok-platform/components/TiktokSyncProgressSlot';
import { buildOpsExportMenu, opsExportLabel } from '@/tiktok-platform/utils/buildOpsExportMenu';
import HoverCopyField from '@/tiktok-platform/modules/products/components/HoverCopyField';
import TiktokProductThumb from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { useGuardedRefresh } from '@/tiktok-platform/hooks/useGuardedRefresh';
import { useTiktokSyncJobs } from '@/tiktok-platform/context/TiktokSyncJobContext';
import { RETURN_ORDER_STATUS_LABELS } from '@/tiktok-platform/constants/tiktokApiCatalog';
import { buildTiktokPath } from '@/tiktok-platform/constants/tiktokRoutes';
import { useTiktokPageShopScope } from '@/tiktok-platform/hooks/useTiktokPageShopScope';
import { tiktokPlatformAPI } from '@/tiktok-platform/api/tiktokPlatformApi';
import { extractTiktokList, extractTiktokTotal, pickField } from '@/tiktok-platform/utils/tiktokResponse';
import { formatSiteListSyncedAt } from '@/tiktok-platform/utils/tiktokSiteList';
import {
  listProductSummary,
} from '@/tiktok-platform/modules/aftersales/returnOrderDisplay';
import { showTiktokConfirm } from '@/tiktok-platform/components/TiktokConfirmDialog';
import { showToast } from '@/utils/toast';
import { readNoticeReturnOrderNo } from '@/tiktok-platform/utils/tiktokNoticeFocusQuery';
import '@/styles/tiktok-platform/tiktok-product-ops.css';

const RETURN_STATUS_KEYS = {
  '待处理': 'rsPending',
  '已拒绝': 'rsRejected',
  '待买家寄回': 'rsAwaitBuyer',
  '买家已寄出': 'rsBuyerShipped',
  '拒绝收货': 'rsRejectReceive',
  '退货成功': 'rsReturnSuccess',
  '已取消': 'rsCancelled',
  '已完成': 'rsCompleted',
  '已关闭': 'rsClosed',
  '申请中': 'rsApplying',
  '卖家已收货': 'rsSellerReceived',
  '已送达': 'rsDelivered',
  '待交接': 'rsAwaitHandover',
  '待TEMU转账': 'rsTemuTransfer',
  '待SHEIN转账': 'rsSheinTransfer',
};

function returnStatusText(code) {
  const raw = RETURN_ORDER_STATUS_LABELS[code];
  const key = raw ? RETURN_STATUS_KEYS[raw] : '';
  if (key) return i18n.t(`returnList.${key}`, { ns: 'workbench' });
  if (raw) return raw;
  return code == null || code === '' ? '—' : String(code);
}

const STATUS_OPTIONS = Object.entries(RETURN_ORDER_STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const PRODUCT_ID_OPTIONS = [
  { value: 'productId', labelKey: 'tiktokCatalog.productId' },
  { value: 'sku', label: 'SKU' },
];

const ORDER_ID_OPTIONS = [
  { value: 'returnOrderNo', labelKey: 'returnList.returnNo' },
  { value: 'orderNo', labelKey: 'returnList.relatedOrder' },
];

const EMPTY_FILTER = {
  orderIdType: 'returnOrderNo',
  orderIdValue: '',
  returnReason: '',
  productIdType: 'productId',
  productIdValue: '',
  timeType: 'apply',
  startTime: '',
  endTime: '',
  status: '',
};

function statusModifier(code) {
  const label = RETURN_ORDER_STATUS_LABELS[code] || String(code || '');
  if (/完成|成功|已签收|已退款/.test(label)) return 'success';
  if (/拒绝|取消|失败|关闭/.test(label)) return 'failed';
  if (/待|处理中|审核|运输/.test(label)) return 'pending';
  return 'pending';
}

function rowKey(row, index) {
  return String(pickField(row, 'returnOrderNo', 'returnNo') || `row-${index}`);
}

export default function ReturnOrderListPage() {
  const { t } = useTranslation('workbench');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
  } = useTiktokPageShopScope();
  const activeShopId = concreteScopeId;

  const [filterDraft, setFilterDraft] = useState(() => ({ ...EMPTY_FILTER }));
  const [query, setQuery] = useState(() => ({
    ...EMPTY_FILTER,
    _nonce: 0,
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TIKTOK_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [signingNo, setSigningNo] = useState('');
  const [batchSigning, setBatchSigning] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncRangeOpen, setSyncRangeOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const pendingSyncRangeRef = useRef(null);

  const { startJob, onTrackDone, isBusy } = useTiktokSyncJobs();

  useEffect(() => {
    setPage(1);
    setRows([]);
    setTotal(0);
    setError('');
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setSelectedKeys(new Set());
  }, [activeShopId]);

  const noticeReturnOrderNo = readNoticeReturnOrderNo(searchParams);
  useEffect(() => {
    if (!noticeReturnOrderNo) return;
    const next = {
      ...EMPTY_FILTER,
      orderIdType: 'returnOrderNo',
      orderIdValue: noticeReturnOrderNo,
    };
    setFilterDraft(next);
    setQuery((prev) => ({ ...next, _nonce: (prev._nonce || 0) + 1 }));
    setPage(1);
  }, [noticeReturnOrderNo, activeShopId]);

  useEffect(() => {
    // 仅筛选条件变化时清空选中；翻页保留跨页勾选
    setSelectedKeys(new Set());
  }, [query._nonce]);

  useEffect(() => {
    if (!activeShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    tiktokPlatformAPI.getShopSyncStatus(activeShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'RETURN');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [activeShopId]);

  const queryParams = useMemo(() => {
    const statusNum = query.status === '' || query.status == null ? null : Number(query.status);
    const idType = query.productIdType === 'sku' ? 'sku' : 'skc';
    const idValue = query.productIdValue || undefined;
    const orderType = query.orderIdType === 'orderNo' ? 'orderNo' : 'returnOrderNo';
    const orderValue = query.orderIdValue || undefined;
    const startDate = pickDatePart(query.startTime);
    const endDate = pickDatePart(query.endTime);
    return {
      page,
      pageSize,
      source: 'cached',
      returnOrderStatus: Number.isFinite(statusNum) ? statusNum : undefined,
      returnOrderNo: orderType === 'returnOrderNo' ? orderValue : undefined,
      orderNo: orderType === 'orderNo' ? orderValue : undefined,
      returnReason: query.returnReason || undefined,
      skc: idType === 'skc' ? idValue : undefined,
      sku: idType === 'sku' ? idValue : undefined,
      timeType: query.timeType || 'apply',
      startTime: toDayStartDateTime(startDate) || undefined,
      endTime: toDayEndDateTime(endDate) || undefined,
      _nonce: query._nonce,
    };
  }, [query, page, pageSize]);

  const load = useCallback(async () => {
    if (!activeShopId) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { _nonce, ...params } = queryParams;
      const res = await tiktokPlatformAPI.listReturnOrders(activeShopId, params);
      const list = extractTiktokList(res);
      setRows(list);
      setTotal(extractTiktokTotal(res, list.length));
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e?.message || t('returnList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeShopId, queryParams, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = useCallback(async () => {
    const range = pendingSyncRangeRef.current;
    if (!activeShopId || !range?.startTime || !range?.endTime) return;
    try {
      const track = await startJob({
        kind: 'returnOrders',
        shopId: activeShopId,
        label: t('returnList.syncUpdate'),
        payload: {
          queryType: 2,
          startTime: range.startTime,
          endTime: range.endTime,
        },
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      if (!result?.ok) return;
      const finishedAt = new Date().toISOString();
      setLastSyncedAt(finishedAt);
      setPersistedSyncedAt(finishedAt);
      setQuery((prev) => ({ ...prev, _nonce: prev._nonce + 1 }));
      setPage(1);
      setSyncRangeOpen(false);
      pendingSyncRangeRef.current = null;
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') {
        return;
      }
      showToast(e?.message || t('returnList.syncFailed'), 'error');
    }
  }, [activeShopId, startJob, onTrackDone, t]);

  const {
    refresh: guardedSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('returnOrders', activeShopId);

  const handleSign = async (returnOrderNo) => {
    if (!activeShopId || !returnOrderNo) return;
    const ok = await showTiktokConfirm({
      title: t('returnList.approveTitle'),
      message: t('returnList.approveOne', { no: returnOrderNo, platform: 'TikTok' }),
      tone: 'warning',
      confirmText: t('returnList.approve'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSigningNo(returnOrderNo);
    try {
      await tiktokPlatformAPI.approveReturn(activeShopId, returnOrderNo, {});
      showToast(t('returnList.approved'), 'success');
      load();
    } catch (e) {
      showToast(e?.message || t('returnList.signFailed'), 'error');
    } finally {
      setSigningNo('');
    }
  };

  const openDetail = (returnOrderNo) => {
    if (!returnOrderNo) return;
    navigate(buildTiktokPath(`orders/returns/${encodeURIComponent(returnOrderNo)}`));
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const onQuery = () => {
    const orderTokens = parseBatchTokens(filterDraft.orderIdValue).slice(0, BATCH_QUERY_LIMIT);
    const productTokens = parseBatchTokens(filterDraft.productIdValue).slice(0, BATCH_QUERY_LIMIT);
    const start = pickDatePart(filterDraft.startTime);
    const end = pickDatePart(filterDraft.endTime);
    const rangeError = validateDateRange(start, end);
    if (rangeError) {
      showToast(rangeError, 'error');
      return;
    }
    if (activeShopId) setLoading(true);
    setPage(1);
    setQuery((prev) => ({
      orderIdType: filterDraft.orderIdType === 'orderNo' ? 'orderNo' : 'returnOrderNo',
      orderIdValue: orderTokens.join(','),
      returnReason: String(filterDraft.returnReason || '').trim(),
      productIdType: filterDraft.productIdType === 'sku' ? 'sku' : 'skc',
      productIdValue: productTokens.join(','),
      timeType: filterDraft.timeType || 'apply',
      startTime: start,
      endTime: end,
      status: filterDraft.status || '',
      _nonce: prev._nonce + 1,
    }));
  };

  const onReset = () => {
    const nextDraft = { ...EMPTY_FILTER };
    setFilterDraft(nextDraft);
    setPage(1);
    setQuery((prev) => ({
      ...nextDraft,
      _nonce: prev._nonce + 1,
    }));
  };

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('returnList.lastUpdate', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('returnList.notSynced');

  const emptyTitle = !activeShopId
    ? t('returnList.pickShop')
    : loading
      ? t('returnList.loading')
      : t('returnList.empty');

  const emptyDesc = !activeShopId || loading
    ? null
    : t('returnList.emptyPull', { platform: 'TikTok' });

  const orderIdLabel = t(ORDER_ID_OPTIONS.find((o) => o.value === filterDraft.orderIdType)?.labelKey || 'returnList.returnNo');
  const productIdOption = PRODUCT_ID_OPTIONS.find((o) => o.value === filterDraft.productIdType);
  const productIdLabel = productIdOption?.label || t(productIdOption?.labelKey || 'tiktokCatalog.productId');
  const batchModalTitle = batchModalField === 'productIdValue'
    ? t('returnList.batchQueryType', { type: productIdLabel })
    : batchModalField === 'orderIdValue'
      ? t('returnList.batchQueryType', { type: orderIdLabel })
      : t('returnList.batchQuery');

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, index)),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedRows = useMemo(() => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row, index) => {
      const key = rowKey(row, index);
      if (selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys)
      .map((key) => map.get(key))
      .filter(Boolean);
  }, [rows, selectedKeys]);


  const buildExportRows = (list) => {
    const headers = [
      'returnOrderNo',
      'orderNo',
      'returnReason',
      'returnOrderStatus',
      'statusLabel',
      'requestReturnTime',
      'sellerSignedTime',
      'completedTime',
      'goodsTitle',
      'skc',
      'skuSn',
      'refundAmount',
    ];
    const csvRows = (Array.isArray(list) ? list : []).map((row) => {
      const statusCode = pickField(row, 'returnOrderStatus', 'status');
      return {
        returnOrderNo: pickField(row, 'returnOrderNo', 'returnNo'),
        orderNo: pickField(row, 'orderNo', 'billNo', 'orderNumber'),
        returnReason: pickField(row, 'returnReason'),
        returnOrderStatus: statusCode,
        statusLabel: returnStatusText(statusCode) || statusCode || '',
        requestReturnTime: pickField(row, 'requestReturnTime', 'applyTime'),
        sellerSignedTime: pickField(row, 'sellerSignedTime'),
        completedTime: pickField(row, 'completedTime'),
        goodsTitle: pickField(row, 'goodsTitle'),
        skc: pickField(row, 'skc'),
        skuSn: pickField(row, 'skuSn'),
        refundAmount: pickField(row, 'refundAmount'),
      };
    });
    return { headers, csvRows };
  };

  const exportRows = (list, emptyMessage) => {
    if (!list.length) {
      showToast(emptyMessage, 'warning');
      return;
    }
    const { headers, csvRows } = buildExportRows(list);
    downloadCsv(`tiktok-return-orders-${Date.now()}.csv`, headers, csvRows);
    showToast(t('returnList.exported', { count: csvRows.length }), 'success');
  };

  const exportSelected = () => {
    exportRows(selectedRows, t('returnList.pickToExport'));
  };

  const exportPage = () => {
    exportRows(rows, t('returnList.noneToExport'));
  };

  const exportFiltered = async () => {
    if (!activeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (total <= 0 && rows.length === 0) {
      showToast(t('returnList.noneToExport'), 'warning');
      return;
    }
    setExporting(true);
    try {
      const { _nonce, ...baseParams } = queryParams;
      const pageSize = 100;
      let pageNo = 1;
      let expectedTotal = Number.isFinite(total) && total > 0 ? total : Infinity;
      const all = [];
      while (all.length < expectedTotal) {
        const res = await tiktokPlatformAPI.listReturnOrders(activeShopId, {
          ...baseParams,
          page: pageNo,
          pageSize,
        });
        const list = extractTiktokList(res);
        expectedTotal = extractTiktokTotal(res, list.length);
        all.push(...list);
        if (!list.length || list.length < pageSize) break;
        pageNo += 1;
        if (pageNo > 500) break;
      }
      exportRows(all, t('returnList.noneToExport'));
    } catch (e) {
      showToast(e?.message || t('returnList.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const batchSignSelected = async () => {
    if (!activeShopId) return;
    if (!selectedRows.length) {
      showToast(t('returnList.pickToSign'), 'warning');
      return;
    }
    const returnOrderNoList = selectedRows
      .map((row) => pickField(row, 'returnOrderNo', 'returnNo'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    if (!returnOrderNoList.length) {
      showToast(t('returnList.missingReturnNo'), 'warning');
      return;
    }
    const ok = await showTiktokConfirm({
      title: t('returnList.batchApprove'),
      message: t('returnList.batchApproveMsg', { count: returnOrderNoList.length, platform: 'TikTok' }),
      tone: 'warning',
      confirmText: t('returnList.approve'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setBatchSigning(true);
    try {
      let successCount = 0;
      let fail = 0;
      for (const returnNo of returnOrderNoList) {
        try {
          await tiktokPlatformAPI.approveReturn(activeShopId, returnNo, {});
          successCount += 1;
        } catch {
          fail += 1;
        }
      }
      const summary = { success: successCount, failed: fail, errors: [] };
      if (successCount > 0) {
        showToast(fail ? t('returnList.batchSignPartial', { ok: successCount, fail }) : t('returnList.batchSignDone', { count: successCount }), fail ? 'warning' : 'success');
        setSelectedKeys(new Set());
        load();
      } else {
        const tip = Array.isArray(summary?.errors) && summary.errors.length
          ? `：${summary.errors.slice(0, 2).join('；')}`
          : (fail ? t('returnList.failCount', { count: fail }) : '');
        showToast(`${t('returnList.batchSignFailed')}${tip}`, 'error');
      }
    } catch (e) {
      showToast(e?.message || t('returnList.batchSignFailed'), 'error');
    } finally {
      setBatchSigning(false);
    }
  };

  const onBatchDelete = async () => {
    if (!activeShopId || selectedKeys.size === 0) {
      showToast(t('returnList.pickToDelete'), 'warning');
      return;
    }
    const returnOrderNos = selectedRows
      .map((row) => pickField(row, 'returnOrderNo', 'returnNo'))
      .filter((no) => no != null && String(no).trim() !== '')
      .map((no) => String(no).trim());
    const ids = selectedRows
      .map((row) => Number(row?.id ?? pickField(row, 'id')))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (returnOrderNos.length === 0 && ids.length === 0) {
      showToast(t('returnList.missingReturnNoDelete'), 'error');
      return;
    }
    const countHint = returnOrderNos.length || ids.length;
    const ok = await showTiktokConfirm({
      title: t('returnList.batchDelete'),
      message: t('returnList.batchDeleteMsg', { count: countHint, platform: 'TikTok' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await tiktokPlatformAPI.batchDeleteReturnOrders(activeShopId, {
        returnOrderNos,
        ids,
      });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : countHint;
      if (count > 0) {
        showToast(t('returnList.deletedCount', { count }), 'success');
      } else {
        showToast(t('returnList.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      load();
    } catch (e) {
      showToast(e?.message || t('returnList.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <TiktokSyncProgressSlot kinds={['returnOrders']} />
      <ProductFilterCard
        toolbar={(
          <TiktokOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || batchSigning || exporting || deleting}
            syncKinds={['returnOrders']}
            onSync={() => {
              if (!activeShopId) {
                showToast(t('returnList.pickAuthorized', { platform: 'TikTok' }), 'warning');
                return;
              }
              setSyncRangeOpen(true);
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={shopLoading || !activeShopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={t('returnList.syncHint', { platform: 'TikTok' })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={exporting || !activeShopId}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!activeShopId || deleting}
            extraActions={[
              {
                key: 'batch-sign',
                label: batchSigning ? t('returnList.processing') : t('returnList.batchApprove'),
                tone: 'primary',
                disabled: selectedKeys.size === 0 || batchSigning,
                onClick: batchSignSelected,
              },
            ]}
          />
        )}
      >
        <div className="tko-filter-bar">
          <div className="tko-filter-bar__fields">
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch tko-field--w300">
              <select
                className="tko-select tko-select--compact tko-select--order-type"
                value={filterDraft.orderIdType}
                disabled={!activeShopId}
                aria-label={t('returnList.idTypeAria')}
                onChange={(e) => updateField('orderIdType', e.target.value)}
              >
                {ORDER_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
              <input
                className="tko-input"
                value={filterDraft.orderIdValue}
                placeholder={t('returnList.idPlaceholder', { type: orderIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('orderIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('returnList.batchInputId', { type: orderIdLabel })}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => setBatchModalField('orderIdValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tko-field tko-field--inline tko-field--id-combo tko-field--batch tko-field--w300">
              <select
                className="tko-select tko-select--compact"
                value={filterDraft.productIdType}
                disabled={!activeShopId}
                aria-label={t('returnList.productIdAria')}
                onChange={(e) => updateField('productIdType', e.target.value)}
              >
                {PRODUCT_ID_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
              <input
                className="tko-input"
                value={filterDraft.productIdValue}
                placeholder={t('returnList.idPlaceholder', { type: productIdLabel })}
                disabled={!activeShopId}
                onChange={(e) => updateField('productIdValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tko-batch-add-btn"
                aria-label={t('returnList.batchInputId', { type: productIdLabel })}
                title={t('returnList.batchQuery')}
                disabled={!activeShopId}
                onClick={() => setBatchModalField('productIdValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            <div className="tko-field tko-field--inline tko-field--status">
              <span className="tko-field__label">{t('returnList.status')}</span>
              <select
                className="tko-select tko-select--status"
                value={filterDraft.status}
                disabled={!activeShopId}
                aria-label={t('returnList.statusAria')}
                onChange={(e) => updateField('status', e.target.value)}
              >
                <option value="">{t('returnList.allStatus')}</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{returnStatusText(opt.value)}</option>
                ))}
              </select>
            </div>
            <div className="tko-field tko-field--inline tko-field--w300">
              <span className="tko-field__label">{t('returnList.reason')}</span>
              <input
                className="tko-input"
                value={filterDraft.returnReason}
                placeholder={t('returnList.reasonPh')}
                disabled={!activeShopId}
                onChange={(e) => updateField('returnReason', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            <TiktokDateRangeFilter
              timeType={filterDraft.timeType}
              startDate={filterDraft.startTime}
              endDate={filterDraft.endTime}
              timeTypeOptions={TIKTOK_RETURN_TIME_TYPE_OPTIONS}
              disabled={!activeShopId}
              onTimeTypeChange={(v) => updateField('timeType', v)}
              onStartDateChange={(v) => updateField('startTime', v)}
              onEndDateChange={(v) => updateField('endTime', v)}
            />
          </div>

          <div className="tko-filter-bar__actions">
            <button
              type="button"
              className="tko-btn tko-btn--secondary tko-btn--sm"
              onClick={onReset}
              disabled={!activeShopId}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tko-btn tko-btn--primary tko-btn--sm"
              onClick={onQuery}
              disabled={!activeShopId || loading}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
        {error ? (
          <div className="tko-ops-error" role="alert">{error}</div>
        ) : null}
      </ProductFilterCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={t('returnList.batchLinePh')}
        onConfirm={(tokens) => {
          if (!batchModalField) return;
          updateField(batchModalField, joinBatchTokens(tokens));
        }}
        onClose={() => setBatchModalField(null)}
      />

      <TiktokSyncRangeModal
        open={syncRangeOpen}
        title={t('returnList.syncReturns')}
        defaultHours={48}
        presets={[
          { label: t('returnList.last24h'), hours: 24 },
          { label: t('returnList.last48h'), hours: 48 },
          { label: t('returnList.last7d'), hours: 24 * 7 },
          { label: t('returnList.last14d'), hours: 24 * 14 },
          { label: t('returnList.last30d'), hours: 24 * 30 },
        ]}
        confirming={syncing}
        onConfirm={async (range) => {
          pendingSyncRangeRef.current = range;
          const ok = await guardedSync();
          if (ok !== false) setSyncRangeOpen(false);
        }}
        onClose={() => {
          if (!syncing) setSyncRangeOpen(false);
        }}
      />

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(activeShopId) && loading}
        loadingLabel={t('returnList.querying')}
      >
        <div className="tko-drafts__table-wrap tko-ops-table-wrap">
          <table className="tko-publish__table tko-drafts__table tko-ops-table tko-ops-table--return">
            <colgroup>
              {/* 复选固定；产品信息约 24%；其余列（含操作）平分剩余宽度，避免固定 px 导致表头脱轨 */}
              <col className="tko-ops-col tko-ops-col--check tiktok-product-col--check" />
              <col className="tko-ops-col tko-ops-col--absorb" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--share" />
              <col className="tko-ops-col tko-ops-col--ops tko-ops-col--share" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tko-ops-th tko-ops-th--check tiktok-product-cell--spu-check"
                  onClick={() => {
                    if (!activeShopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('returnList.selectAll')}
                    disabled={!activeShopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col">{t('returnList.productInfo')}</th>
                <th scope="col">{t('returnList.returnNo')}</th>
                <th scope="col">{t('returnList.relatedOrder')}</th>
                <th scope="col">{t('returnList.reason')}</th>
                <th scope="col">{t('returnList.status')}</th>
                <th scope="col">{t('returnList.time')}</th>
                <th scope="col" className="tko-ops-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!activeShopId || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="tko-certs-empty-cell">
                    <div className="tko-certs-empty">
                      <p className="tko-certs-empty__title">{emptyTitle}</p>
                      {emptyDesc ? <p className="tko-certs-empty__desc">{emptyDesc}</p> : null}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const key = rowKey(row, index);
                  const no = pickField(row, 'returnOrderNo', 'returnNo');
                  const relatedOrderNo = pickField(row, 'orderNo', 'billNo', 'orderNumber');
                  const statusCode = pickField(row, 'returnOrderStatus', 'status');
                  const statusLabel = returnStatusText(statusCode);
                  const product = listProductSummary(row);
                  const returnReason = pickField(row, 'returnReason') || '—';
                  return (
                    <tr key={key}>
                      <td
                        className="tko-ops-td tko-ops-td--check tiktok-product-cell--spu-check"
                        onClick={() => toggleOne(key)}
                      >
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: no || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="tko-ops-td tko-ops-td--product">
                        {product.title || product.imageUrl || product.productId || product.merchantSku || product.refundText ? (
                          <div className="tko-return-product">
                            {product.imageUrl ? (
                              <TiktokProductThumb
                                imageUrl={product.imageUrl}
                                className="tko-return-product__img"
                                placeholderClassName="tko-return-product__img tko-return-product__img--empty"
                                alt={product.title || t('returnList.productAlt')}
                              />
                            ) : (
                              <div className="tko-return-product__img tko-return-product__img--empty" />
                            )}
                            <div className="tko-return-product__meta">
                              <div className="tko-return-product__title" title={product.title || undefined}>
                                {product.title || '—'}
                                {product.goodsCount > 1 ? (
                                  <span className="tko-return-product__count">{t('returnList.moreItems', { count: product.goodsCount })}</span>
                                ) : null}
                              </div>
                              <div className="tko-return-product__sku">
                                {t('tiktokCatalog.productId')}：{product.productId || '—'}
                              </div>
                              <div className="tko-return-product__sku">
                                {t('returnList.merchantSkuPrefix')}{product.merchantSku || '—'}
                              </div>
                              <div className="tko-return-product__refund">
                                {t('returnList.refundPrefix')}{product.refundText || '—'}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="tko-return-product__empty">{t('returnList.noProduct')}</span>
                        )}
                      </td>
                      <td className="tko-ops-td tko-ops-td--orderno">
                        <HoverCopyField
                          className="tko-return-copy"
                          value={no}
                          display={no ? <strong>{no}</strong> : '—'}
                        />
                      </td>
                      <td className="tko-ops-td tko-ops-td--related">
                        <HoverCopyField
                          className="tko-return-copy"
                          value={relatedOrderNo}
                          display={relatedOrderNo || '—'}
                        />
                      </td>
                      <td className="tko-ops-td tko-ops-td--reason" title={returnReason === '—' ? undefined : returnReason}>
                        {returnReason}
                      </td>
                      <td>
                        <span className={`tko-records__status tko-records__status--${statusModifier(statusCode)}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="tko-ops-td tko-ops-td--times">
                        <div className="tko-return-times">
                          <div>
                            <span className="tko-return-times__label">{t('returnList.applied')}</span>
                            <span>
                              {pickField(row, 'requestReturnTime', 'applyTime', 'createTime', 'returnTime') || '—'}
                            </span>
                          </div>
                          <div>
                            <span className="tko-return-times__label">{t('returnList.signed')}</span>
                            <span>{pickField(row, 'sellerSignedTime') || '—'}</span>
                          </div>
                          <div>
                            <span className="tko-return-times__label">{t('returnList.completed')}</span>
                            <span>{pickField(row, 'completedTime') || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="tko-ops-td tko-ops-td--ops tko-certs-td tko-certs-td--ops">
                        <div className="tko-drafts__actions">
                          <button
                            type="button"
                            className="tko-btn tko-btn--secondary tko-btn--sm"
                            disabled={!no}
                            onClick={() => openDetail(no)}
                          >
                            {t('returnList.detail')}
                          </button>
                          <button
                            type="button"
                            className="tko-btn tko-btn--primary tko-btn--sm"
                            disabled={!no || signingNo === no || batchSigning}
                            onClick={() => handleSign(no)}
                          >
                            {signingNo === no ? t('returnList.submitting') : t('returnList.approve')}
                          </button>
                          <button
                            type="button"
                            className="tko-btn tko-btn--secondary tko-btn--sm"
                            disabled={!no || signingNo === no || batchSigning}
                            onClick={async () => {
                              if (!activeShopId || !no) return;
                              const ok = await showTiktokConfirm({
                                title: t('returnList.rejectTitle'),
                                message: t('returnList.rejectOne', { no }),
                                tone: 'warning',
                                confirmText: t('returnList.reject'),
                                cancelText: t('shop.cancel'),
                              });
                              if (!ok) return;
                              try {
                                await tiktokPlatformAPI.rejectReturn(activeShopId, no, {
                                  decision: 'REJECT_REFUND',
                                  reject_reason: 'seller_reject',
                                });
                                showToast(t('returnList.rejected'), 'success');
                                load();
                              } catch (e) {
                                showToast(e?.message || t('returnList.rejectFailed'), 'error');
                              }
                            }}
                          >
                            {t('returnList.reject')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <TiktokOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!activeShopId}
          pageItemCount={rows.length}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('returnList.pager')}
        />
      </ProductListCard>
    </>
  );
}
